import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, updateInventoryCategory } from '../core/_requests'
import { CompanyGroupAdmin, InventoryCategory } from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryCrudAccess } from '../core/libraryPermissionAccess'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditCategoryModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  category: InventoryCategory | null
}

const EditCategoryModal: FC<EditCategoryModalProps> = ({ visible, onClose, onSubmit, category }) => {
  const { currentUser } = useAuth()
  const { can, getScope } = useProcurementPermissions()
  const canUpdate = libraryCrudAccess.category.update(can)
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.UPDATE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({ name: '', company: '' })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  useEffect(() => {
    if (visible && category) {
      setFormData({
        name: category.name || '',
        company: category.cgaid?.id?.toString() || '',
      })
    }
  }, [visible, category])

  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    }
  }, [visible, libraryScopeAll])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!formData.name.trim()) newErrors.name = 'Category Name is required'
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!canUpdate) {
      toast.error('You do not have permission to update categories.')
      return
    }
    if (!validateForm() || !category) return
    setIsSubmitting(true)
    try {
      let cgaIdToUse: number
      if (libraryScopeAll) {
        cgaIdToUse = parseInt(formData.company, 10)
      } else if (libraryScopeCompany && effectiveCgaId) {
        cgaIdToUse = effectiveCgaId
      } else {
        const tid = tenantCgaId
        if (!tid) {
          toast.error('Unable to resolve company scope for this user')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = tid
      }

      await updateInventoryCategory(category.id, {
        name: formData.name,
        cgaid: { id: cgaIdToUse },
        caid: (category as any).caid ?? { id: null },
        vesselId: undefined,
        vesselType: undefined,
      })
      toast.success('Category updated successfully!')
      onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error updating category:', error)
      toast.error('Failed to update category')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', company: '' })
    setErrors({})
    setCompanies([])
    onClose()
  }

  if (!visible || !category || !canUpdate) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Edit Category</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            <div className='row g-3'>
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                    Company
                  </label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              <div className='col-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                  Category Name
                </label>
                <input
                  type='text'
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='Enter category name'
                  style={{ color: '#000' }}
                />
                {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
              </div>

              {libraryScopeCompany && effectiveCgaId && (
                <div className='col-12'>
                  <div className='alert alert-info'>
                    <i className='fas fa-info-circle me-2'></i>
                    Category is company-wide under your company group (ID: {effectiveCgaId}).
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Category'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditCategoryModal
